import React from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import { getIntlTranslation } from '../../../../../../utils/helper';
import {
  getRiskTypeDisplayText,
  getDefectTypeDisplayText,
  getPriorityTypeDisplayText,
  calulateTotal
} from '../../defectHelper';

/**
 * FormSummary - Sticky bottom bar showing current form state
 * Extracted from EnhancedDefectForm.js to improve maintainability
 * 
 * @param {Object} values - Formik values
 * @param {Array} riskTypeOptions - Risk options for display
 * @param {Array} defectTypeOptions - Defect type options
 * @param {Array} priorityTypeOptions - Priority options
 * @param {string} projectLanguageCode - Project language (de/en)
 * @param {boolean} isElementOther - Whether "Others" is selected
 * @param {Function} getElementTypeLabel - Function to get element label
 */
const FormSummary = React.memo(({
  values,
  riskTypeOptions,
  defectTypeOptions,
  priorityTypeOptions,
  projectLanguageCode,
  isElementOther,
  getElementTypeLabel
}) => {
  // Only show if any field has data
  const hasData = values.elementType || values.riskType || 
                  values.priorityType || values.priority || values.defectType;
  
  if (!hasData) return null;
  
  // Get element display text
  const elementLabel = isElementOther 
    ? (values.customElement?.value || values.customElement?.title || getIntlTranslation('enter-custom-name'))
    : getElementTypeLabel(values.elementType);
  
  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        bgcolor: 'white',
        borderTop: 1,
        borderColor: 'grey.300',
        py: 1,
        px: 3,
        zIndex: 1000,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)'
      }}
    >
      <Container maxWidth="xl">
        <Stack direction="row" spacing={3} alignItems="center" sx={{ minHeight: 28 }}>
          <SummaryField 
            label={getIntlTranslation('_element-type')} 
            value={elementLabel} 
          />
          <SummaryField 
            label={getIntlTranslation('_risk-Type')} 
            value={getRiskTypeDisplayText(values.riskType, riskTypeOptions, projectLanguageCode) || getIntlTranslation('not-selected')} 
          />
          <SummaryField 
            label={getIntlTranslation('_Priority')} 
            value={getPriorityTypeDisplayText(values.priorityType || values.priority, priorityTypeOptions, projectLanguageCode) || getIntlTranslation('not-selected')} 
          />
          <SummaryField 
            label={getIntlTranslation('_Type')} 
            value={getDefectTypeDisplayText(values.defectType, defectTypeOptions, projectLanguageCode) || getIntlTranslation('not-selected')} 
          />
          {values.costDefect && (
            <SummaryField 
              label={getIntlTranslation('_cost')} 
              value={`€${calulateTotal(values, false) || 0}`} 
            />
          )}
        </Stack>
      </Container>
    </Box>
  );
});

FormSummary.displayName = 'FormSummary';

// Helper component for consistent field display
const SummaryField = ({ label, value }) => (
  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
    <Typography component="span" sx={{ fontWeight: 'bold' }}>
      {label}:
    </Typography>{' '}
    {value}
  </Typography>
);

export default FormSummary;
